import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../CSS/LeaguePage.css"; // Import the CSS file
import { supabase } from "../supabase";

const LeaguePage = () => {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeagueData = async () => {
      // Fetch league information
      const { data: leagueData, error: leagueError } = await supabase
        .from("leagues")
        .select("*")
        .eq("league_id", leagueId)
        .single();

      if (leagueError) {
        console.error("Error fetching league:", leagueError);
        setLoading(false);
        return;
      }

      setLeague(leagueData);

      // Fetch players information
      const { data: playersData, error: playersError } = await supabase
        .from("leagueplayers")
        .select("*")
        .eq("league_id", leagueId);

      if (playersError) {
        console.error("Error fetching players:", playersError);
        setLoading(false);
        return;
      }

      console.log("Fetched players data:", playersData); // Debugging log
      setPlayers(playersData);

      // Extract team IDs from players data
      const teamIds = playersData.flatMap((player) => {
        return player.teams
          .map((teamString) => {
            try {
              const team = JSON.parse(teamString);
              return parseInt(team.id, 10); // Convert team ID to integer
            } catch (error) {
              console.error("Error parsing team JSON:", error);
              return null;
            }
          })
          .filter((id) => id !== null);
      });

      console.log("Team IDs:", teamIds); // Debugging log

      // Fetch teams information
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams") // Ensure the table name is correct
        .select("*")
        .in("team_id", teamIds); // Use the correct column name

      if (teamsError) {
        console.error("Error fetching teams:", teamsError);
        setLoading(false);
        return;
      }

      console.log("Fetched teams data:", teamsData); // Debugging log
      setTeams(teamsData);

      setLoading(false);
    };

    fetchLeagueData();
  }, [leagueId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!league) {
    return <div>No league data found.</div>;
  }

  const getTeamInfo = (teamId) => {
    const team = teams.find((t) => t.team_id === teamId);
    console.log("Team ID: ", teamId); // Debugging log
    console.log("Team: ", team); // Debugging log

    return team
      ? { name: team.team_name, wins: team.wins, losses: team.losses }
      : { name: "", wins: 0, losses: 0 };
  };

  // Calculate total wins for each player
  const playersWithTotalWins = players.map((player) => {
    const totalWins = player.teams.reduce((acc, teamString) => {
      try {
        const team = JSON.parse(teamString);
        const teamInfo = getTeamInfo(parseInt(team.id, 10));
        return acc + teamInfo.wins;
      } catch (error) {
        console.error("Error parsing team JSON:", error);
        return acc;
      }
    }, 0);
    return { ...player, totalWins };
  });

  // Sort players by total wins in descending order
  const sortedPlayers = playersWithTotalWins.sort(
    (a, b) => b.totalWins - a.totalWins
  );

  return (
    <div className="league-page">
      <h1>{league.league_name}</h1>
      <div className="navbar">NBA League Standings</div>
      <table className="standings-table">
        <thead>
          <tr>
            <th>Position</th>
            <th>Player</th>
            <th>Wins</th>
          </tr>
        </thead>
        <tbody>
          {sortedPlayers.map((player, index) => (
            <tr key={player.id}>
              <td className="position">{index + 1}</td>
              <td>{player.player_name}</td>
              <td>{player.totalWins}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="league-table">
        <thead>
          <tr>
            <th>Player</th>
            <th>Team</th>
            <th>Win</th>
            <th>Loss</th>
          </tr>
        </thead>
        <tbody>
          {players.map((player, playerIndex) => {
            console.log("Player:", player); // Debugging log
            const playerTeams = player.teams.map((teamString) => {
              try {
                const team = JSON.parse(teamString);
                return getTeamInfo(parseInt(team.id, 10)); // Convert team ID to integer
              } catch (error) {
                console.error("Error parsing team JSON:", error);
                return { name: "", wins: 0, losses: 0 };
              }
            });
            const totalWins = playerTeams.reduce(
              (acc, team) => acc + team.wins,
              0
            );
            const totalLosses = playerTeams.reduce(
              (acc, team) => acc + team.losses,
              0
            );

            return (
              <React.Fragment key={playerIndex}>
                {playerTeams.map((team, teamIndex) => (
                  <tr key={`${playerIndex}-${teamIndex}`}>
                    <td className="player-name">
                      {teamIndex === 0 ? player.player_name : ""}
                    </td>
                    <td>{team.name}</td>
                    <td>{team.wins}</td>
                    <td>{team.losses}</td>
                  </tr>
                ))}
                <tr className="player-total">
                  <td colSpan="2">Win Total: {totalWins}</td>
                  <td colSpan="2">Loss Total: {totalLosses}</td>
                </tr>
                <tr className="player-gap">
                  <td colSpan="4"></td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LeaguePage;
